import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import { WidgetId } from 'features/types'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useOverflowObserver } from 'hooks/useOverflowObserver'
import { Theme } from 'features/ui/types'
import { ColDefs } from 'components/visualization/Table'
import StrechTable from 'components/visualization/Table/StrechTable'
import ViewOption from 'enums/InsightViewOption'
// @ts-ignore
import { Tooltip } from '@netbasequid/core'
import {
  SideBySideWidgetsType,
  SideBySideData,
  SideBySideSimplifiedData,
  SideBySideSelectionType
} from '../types'

import useSideBySideData from './hooks/useSideBySideData'
import InsightChart from './InsightChart'
import { VOC_DEFAULT_CELL_HEIGHT, INSIGHT_CHART_MIN_WIDTH } from './constants'

interface StyledNameContainerProps {
  idealHeight: number
}

const StyledNameContainer = styled.div<StyledNameContainerProps>`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${({ idealHeight }) => Math.floor(idealHeight / 22)};
  -webkit-box-orient: vertical;
  word-break: break-word;
  overflow-wrap: break-word;
`

const NameContainer = ({
  children,
  mode,
  idealHeight
}: {
  children: React.ReactNode
  mode: Theme
  idealHeight: number
}) => {
  const { isOverflowY, targetRef } = useOverflowObserver()

  const handleRef = (
    setTooltipTriggerRef: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  ) => (StyledNameContainerRef: HTMLDivElement) => {
    targetRef.current = StyledNameContainerRef
    setTooltipTriggerRef(StyledNameContainerRef)
  }

  return (
    <Tooltip enabled={isOverflowY} content={children} mode={mode} callbackRefCreator={handleRef}>
      <StyledNameContainer idealHeight={idealHeight}>{children}</StyledNameContainer>
    </Tooltip>
  )
}

const getChartColumnInfo = (widgetSetting: SideBySideWidgetsType) => {
  const { pivotThemeInfos, tagNames, docNames } = widgetSetting
  const themeLength = pivotThemeInfos?.length ?? 0
  const tagLength = tagNames?.length ?? 0
  const docLength = docNames?.length ?? 0

  const columnVariableAmount = themeLength + tagLength + docLength

  return {
    chartColumnAmount: columnVariableAmount || 1,
    hasHeader: columnVariableAmount > 0
  }
}

const StyledHeaderContainer = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  overflow-wrap: break-word;
  pointer-events: none; // * prevent browser Tooltip over Truncated Text in Safari
`

const HeaderContainer = ({ children, mode }: { children: React.ReactNode; mode: Theme }) => {
  const { isOverflowX, targetRef } = useOverflowObserver()

  return (
    <Tooltip enabled={isOverflowX} content={children} mode={mode}>
      <div>
        <StyledHeaderContainer ref={targetRef as React.MutableRefObject<HTMLDivElement>}>
          {children}
        </StyledHeaderContainer>
      </div>
    </Tooltip>
  )
}

const getHeaderName = (
  widgetSetting: SideBySideWidgetsType,
  insightData: SideBySideSimplifiedData
) => {
  const { pivotThemeInfos, tagNames, docNames } = widgetSetting
  const themeLength = pivotThemeInfos?.length ?? 0
  const tagLength = tagNames?.length ?? 0
  const docLength = docNames?.length ?? 0

  if (themeLength > 0) {
    return pivotThemeInfos?.find((theme) => theme.themeId === insightData?.themeId)?.name ?? ''
  }
  if (tagLength > 0) {
    return insightData?.tagName ?? ''
  }
  if (docLength > 0) {
    return insightData?.docName ?? ''
  }
  return ''
}

const generateColumnConfig = ({
  rowData,
  cellHeight,
  widgetSetting,
  onCellClick
}: {
  rowData: SideBySideData['simplifiedData']
  cellHeight: number
  widgetSetting: SideBySideWidgetsType
  onCellClick: (target: SideBySideSelectionType) => void
}) => {
  const { chartColumnAmount } = getChartColumnInfo(widgetSetting)
  // * dynamic column
  const colDefs: ColDefs<typeof rowData[number]> = [
    {
      field: 'topicName',
      headerText: '',
      cellProps: {
        textAlign: 'left',
        displayTooltip: false
      },
      customCellRender: (cellValue, { theme }) => (
        <NameContainer idealHeight={cellHeight} mode={theme}>
          {cellValue}
        </NameContainer>
      )
    }
  ]
  for (let i = 0; i < chartColumnAmount; i += 1) {
    colDefs.push({
      field: `datas${i}`,
      cellProps: {
        textAlign: 'left',
        displayTooltip: false
      },
      customCellRender: (__, { row }) => (
        <InsightChart widgetSetting={widgetSetting} data={row.datas[i]} onCellClick={onCellClick} />
      ),
      customHeaderRender: ({ theme }) => (
        <HeaderContainer mode={theme}>
          {getHeaderName(widgetSetting, rowData[0]?.datas[i])}
        </HeaderContainer>
      )
    })
  }
  return colDefs
}

const getDefaultCellHeight = (widgetSetting: SideBySideWidgetsType) => {
  const { topN, viewOption } = widgetSetting
  switch (viewOption) {
    case ViewOption.STAR_RATINGS:
    case ViewOption.SURVEY_SCORES:
      return VOC_DEFAULT_CELL_HEIGHT
    default:
      return (24 + 4) * (topN! - 1) + 24 // * e.g topN = 5, cellheight 136
  }
}

const SideBySide: ({
  widgetId,
  onCellClick
}: {
  widgetId: WidgetId
  onCellClick: (target: SideBySideSelectionType) => void
}) => React.ReactElement = ({ widgetId, onCellClick }) => {
  const widgetSetting = useDashboardWidgetSetting<SideBySideWidgetsType>(widgetId)
  const sideBySideData = useSideBySideData(widgetId)
  const defaultCellHeight = getDefaultCellHeight(widgetSetting)
  const { hasHeader } = getChartColumnInfo(widgetSetting)
  const [cellHeight, setCellHeight] = useState(defaultCellHeight)

  const rowData = sideBySideData?.simplifiedData ?? []

  const colDefs = generateColumnConfig({
    rowData,
    cellHeight,
    widgetSetting,
    onCellClick
  })

  const handleCellHeightChange = useCallback(
    (newCellHeight: number) => {
      if (cellHeight !== newCellHeight) setCellHeight(newCellHeight)
    },
    [cellHeight]
  )

  return (
    <StrechTable
      rowData={rowData}
      colDefs={colDefs}
      isDisplayFoot={false}
      // * new added props
      defaultCellHeight={cellHeight}
      onCellHeightChange={handleCellHeightChange}
      isDisplayHead={hasHeader}
      defaultCellWidth={INSIGHT_CHART_MIN_WIDTH}
      noHeaderOffset={6}
    />
  )
}

export default SideBySide
